import { ApolloClient, HttpLink, InMemoryCache, from, type ApolloLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { mockLink } from './api/mockLink';

export type ApiTokenProvider = () => string | null | undefined;

// Machine-to-machine API key provided at build/launch time via VITE_API_KEY.
const envApiKey = import.meta.env.VITE_API_KEY as string | undefined;

// Base URL for the backend. In dev, leave empty so Vite's proxy (/api → http://localhost:17000) handles it.
const baseURL = (import.meta.env.VITE_HD_ERP_BACKEND_URL as string | undefined) ?? '';
const apiEndpoint = `${baseURL}/api/restricted`;

// Backend-less mode for firewalled / standalone deployments.
// When enabled, all GraphQL operations are served by `mockLink` from in-memory JSON.
const useMockApi = (import.meta.env.VITE_USE_MOCK_API ?? 'false').toString() === 'true';

if (useMockApi && typeof console !== 'undefined') {
    console.info('[apollo] VITE_USE_MOCK_API=true — using in-memory mock data, no network calls');
}

const networkLink = (): ApolloLink => (useMockApi ? mockLink : new HttpLink({ uri: apiEndpoint }));

// User session token from localStorage (set after login).
const readTokenFromStorage: ApiTokenProvider = () => {
    if (typeof window === 'undefined') return null;

    return (
        window.localStorage.getItem('hd_api_token') ??
        window.localStorage.getItem('api_token') ??
        window.localStorage.getItem('configurator_user_token')
    );
};

const defaultCache = () => new InMemoryCache();

/**
 * Client for unauthenticated GraphQL queries (uses VITE_API_KEY).
 * All public data fetching (ring templates, alloys, gems) goes here.
 */
export const publicClient = new ApolloClient({
    link: from([
        setContext((_, previousContext) => {
            if (!envApiKey) return previousContext;
            return {
                ...previousContext,
                headers: { ...(previousContext.headers ?? {}), api_token: envApiKey },
            };
        }),
        networkLink(),
    ]),
    cache: defaultCache(),
    defaultOptions: {
        watchQuery: { fetchPolicy: useMockApi ? 'no-cache' : 'cache-and-network' },
        query: { fetchPolicy: useMockApi ? 'no-cache' : 'cache-first' },
    },
});

/**
 * Restricted GraphQL client for authenticated user actions.
 * Prefers the user session token from localStorage, falls back to VITE_API_KEY.
 */
export const createRestrictedClient = (getApiToken: ApiTokenProvider = readTokenFromStorage) => {
    const authLink = setContext((_, previousContext) => {
        const token = getApiToken() ?? envApiKey;
        if (!token) return previousContext;

        return {
            ...previousContext,
            headers: {
                ...(previousContext.headers ?? {}),
                api_token: token,
            },
        };
    });

    return new ApolloClient({
        link: from([authLink, networkLink()]),
        cache: defaultCache(),
        defaultOptions: {
            watchQuery: { fetchPolicy: 'cache-and-network' },
        },
    });
};

export const restrictedClient = createRestrictedClient();

// Backward-compatible default export.
export default publicClient;
